import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PayoutScheduling = () => {
  const [scheduleType, setScheduleType] = useState('automatic');
  const [frequency, setFrequency] = useState('weekly');
  const [dayOfWeek, setDayOfWeek] = useState('friday');
  const [dayOfMonth, setDayOfMonth] = useState('15');
  const [minimumAmount, setMinimumAmount] = useState('100');
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveSchedule = async () => {
    setIsSaving(true);
    // Simulate API call
    setTimeout(() => {
      setIsSaving(false);
      alert('Payout schedule updated successfully!');
    }, 1000);
  };

  return (
    <div className="space-y-6">
      {/* Schedule Type Selection */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <h2 className="font-heading text-2xl font-semibold text-foreground mb-6 flex items-center space-x-2">
          <Icon name="Calendar" size={24} strokeWidth={2} className="text-primary" />
          <span>Payout Schedule</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <button
            onClick={() => setScheduleType('automatic')}
            className={`p-6 border-2 rounded-lg transition-smooth text-left ${
              scheduleType === 'automatic' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            }`}
          >
            <div className="flex items-start space-x-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                scheduleType === 'automatic' ? 'bg-primary' : 'bg-muted'
              }`}>
                <Icon name="Repeat" size={24} color={scheduleType === 'automatic' ? 'white' : 'currentColor'} strokeWidth={2} />
              </div>
              <div className="flex-1">
                <h3 className="font-body font-semibold text-foreground mb-2">Automatic Transfers</h3>
                <p className="font-body text-sm text-muted-foreground">
                  Set up recurring automatic payouts on a weekly or monthly schedule
                </p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setScheduleType('manual')}
            className={`p-6 border-2 rounded-lg transition-smooth text-left ${
              scheduleType === 'manual' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            }`}
          >
            <div className="flex items-start space-x-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                scheduleType === 'manual' ? 'bg-primary' : 'bg-muted'
              }`}>
                <Icon name="Hand" size={24} color={scheduleType === 'manual' ? 'white' : 'currentColor'} strokeWidth={2} />
              </div>
              <div className="flex-1">
                <h3 className="font-body font-semibold text-foreground mb-2">Manual Withdrawals</h3>
                <p className="font-body text-sm text-muted-foreground">
                  Request payouts manually whenever you want to withdraw funds
                </p>
              </div>
            </div>
          </button>
        </div>

        {/* Automatic Schedule Settings */}
        {scheduleType === 'automatic' && (
          <div className="space-y-6 p-6 bg-muted rounded-lg">
            <div>
              <label className="font-body text-sm font-medium text-foreground mb-3 block">
                Payout Frequency
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  onClick={() => setFrequency('weekly')}
                  className={`p-4 border-2 rounded-lg transition-smooth ${
                    frequency === 'weekly' ?'border-primary bg-card' :'border-border bg-card hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Icon name="Calendar" size={20} className={frequency === 'weekly' ? 'text-primary' : 'text-muted-foreground'} strokeWidth={2} />
                    <div className="text-left">
                      <p className="font-body font-medium text-foreground">Weekly</p>
                      <p className="font-caption text-xs text-muted-foreground">Every week on a specific day</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setFrequency('monthly')}
                  className={`p-4 border-2 rounded-lg transition-smooth ${
                    frequency === 'monthly' ?'border-primary bg-card' :'border-border bg-card hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Icon name="CalendarDays" size={20} className={frequency === 'monthly' ? 'text-primary' : 'text-muted-foreground'} strokeWidth={2} />
                    <div className="text-left">
                      <p className="font-body font-medium text-foreground">Monthly</p>
                      <p className="font-caption text-xs text-muted-foreground">Once a month on a specific date</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {frequency === 'weekly' && (
              <div>
                <label className="font-body text-sm font-medium text-foreground mb-2 block">
                  Day of Week
                </label>
                <select
                  value={dayOfWeek}
                  onChange={(e) => setDayOfWeek(e?.target?.value)}
                  className="w-full px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-card"
                >
                  <option value="monday">Monday</option>
                  <option value="tuesday">Tuesday</option>
                  <option value="wednesday">Wednesday</option>
                  <option value="thursday">Thursday</option>
                  <option value="friday">Friday</option>
                </select>
                <p className="font-caption text-xs text-muted-foreground mt-2">
                  Payouts will be processed every {dayOfWeek?.charAt(0)?.toUpperCase() + dayOfWeek?.slice(1)}
                </p>
              </div>
            )}

            {frequency === 'monthly' && (
              <div>
                <label className="font-body text-sm font-medium text-foreground mb-2 block">
                  Day of Month
                </label>
                <select
                  value={dayOfMonth}
                  onChange={(e) => setDayOfMonth(e?.target?.value)}
                  className="w-full px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-card"
                >
                  {Array.from({ length: 28 }, (_, i) => i + 1)?.map((day) => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
                <p className="font-caption text-xs text-muted-foreground mt-2">
                  Payouts will be processed on the {dayOfMonth}{dayOfMonth === '1' ? 'st' : dayOfMonth === '2' ? 'nd' : dayOfMonth === '3' ? 'rd' : 'th'} of each month
                </p>
              </div>
            )}

            <div>
              <label className="font-body text-sm font-medium text-foreground mb-2 block">
                Minimum Payout Amount
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">$</span>
                <input
                  type="number"
                  value={minimumAmount}
                  onChange={(e) => setMinimumAmount(e?.target?.value)}
                  className="w-full pl-8 pr-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-card"
                />
              </div>
              <p className="font-caption text-xs text-muted-foreground mt-2">
                Automatic payouts will only process if your balance exceeds this amount
              </p>
            </div>
          </div>
        )}

        {/* Manual Withdrawal Info */}
        {scheduleType === 'manual' && (
          <div className="p-6 bg-muted rounded-lg">
            <div className="flex items-start space-x-4 mb-4">
              <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-1" strokeWidth={2} />
              <div>
                <h3 className="font-body font-semibold text-foreground mb-2">Manual Withdrawal Process</h3>
                <ul className="space-y-2 font-body text-sm text-muted-foreground">
                  <li className="flex items-start space-x-2">
                    <Icon name="Check" size={14} className="text-success mt-1 flex-shrink-0" strokeWidth={2} />
                    <span>Request withdrawals anytime from the Withdrawal History tab</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="Check" size={14} className="text-success mt-1 flex-shrink-0" strokeWidth={2} />
                    <span>Minimum withdrawal amount: $50</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="Check" size={14} className="text-success mt-1 flex-shrink-0" strokeWidth={2} />
                    <span>Processing time: 1-3 business days</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="Check" size={14} className="text-success mt-1 flex-shrink-0" strokeWidth={2} />
                    <span>No fees for standard withdrawals</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end pt-6">
          <Button
            variant="default"
            size="lg"
            iconName="Save"
            onClick={handleSaveSchedule}
            loading={isSaving}
          >
            Save Schedule
          </Button>
        </div>
      </div>

      {/* Processing Time Estimates */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <h2 className="font-heading text-xl font-semibold text-foreground mb-4 flex items-center space-x-2">
          <Icon name="Clock" size={20} strokeWidth={2} className="text-warning" />
          <span>Processing Time Estimates</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 border border-border rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Zap" size={18} className="text-success" strokeWidth={2} />
              <h3 className="font-body font-medium text-foreground">Instant</h3>
            </div>
            <p className="font-body text-sm text-muted-foreground mb-2">Available for verified accounts</p>
            <p className="font-body text-xs text-muted-foreground">Fee: 1.5% of amount</p>
          </div>

          <div className="p-4 border border-border rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Clock" size={18} className="text-primary" strokeWidth={2} />
              <h3 className="font-body font-medium text-foreground">Standard</h3>
            </div>
            <p className="font-body text-sm text-muted-foreground mb-2">1-3 business days</p>
            <p className="font-body text-xs text-success">Fee: Free</p>
          </div>

          <div className="p-4 border border-border rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Calendar" size={18} className="text-accent" strokeWidth={2} />
              <h3 className="font-body font-medium text-foreground">Scheduled</h3>
            </div>
            <p className="font-body text-sm text-muted-foreground mb-2">Automatic on set date</p>
            <p className="font-body text-xs text-success">Fee: Free</p>
          </div>
        </div>
      </div>

      {/* Next Scheduled Payout */}
      {scheduleType === 'automatic' && (
        <div className="bg-gradient-to-br from-success/10 to-success/5 border border-success/20 rounded-lg p-6">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-success rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="Calendar" size={24} color="white" strokeWidth={2} />
            </div>
            <div className="flex-1">
              <h3 className="font-body font-semibold text-foreground mb-2">Next Scheduled Payout</h3>
              <p className="font-body text-sm text-muted-foreground mb-3">
                Your next automatic payout is scheduled for{' '}
                <span className="font-bold text-foreground">
                  {frequency === 'weekly' ? `${dayOfWeek?.charAt(0)?.toUpperCase() + dayOfWeek?.slice(1)}, February 14, 2026` : `February ${dayOfMonth}, 2026`}
                </span>
              </p>
              <div className="flex items-center space-x-4 text-sm">
                <div className="flex items-center space-x-2">
                  <Icon name="DollarSign" size={14} className="text-muted-foreground" strokeWidth={2} />
                  <span className="font-body text-muted-foreground">Estimated amount: <span className="font-bold text-foreground">$3,200</span></span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Clock" size={14} className="text-muted-foreground" strokeWidth={2} />
                  <span className="font-body text-muted-foreground">Processing: 1-3 days</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutScheduling;